// The field-wide skins board — every entrant in the tournament pooled into one skins game,
// independent of tiered scoring teams (TeamStandings) and any foursome's own round_games
// skins pool. Used by both the console's live-progress preview (web/src/pages/EventPage.tsx)
// and the public board (web/src/pages/LeaderboardPage.tsx), which is why it carries its own
// tiny stylesheet rather than reaching for `.ev-*`/`.lb-*` — only one of those two sheets is
// ever loaded on a given page, the same convention TeamStandings/TeamCashStandings follow.
//
// Presentation only. The pool itself comes from the shared engine (src/engine/fieldSkins.ts)
// — a caller-built input on the console, `fieldSkinsFromPayload` (src/sync/leaderboard.ts) on
// the public board — so the two boards can never disagree about who has won a skin.

import { fmtMoney } from '@shared/sync/toDomain';
import type { FieldSkinsResult } from '@shared/engine/fieldSkins';

/** "PJ" for "Patrick Jones", the single initial for a one-word name — same convention
 *  TeamCashStandings' own `initials` uses; a hole strip has no room for a full name. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '—';
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

export function FieldSkinsBoard({
  result,
  basis,
  unit = 'player',
}: {
  result: FieldSkinsResult;
  basis: 'gross' | 'net';
  /** 'player' (default): every row is one entrant. 'team': a row is a team (see
   *  src/sync/leaderboard.ts's `fieldSkinsFromPayload` and 0025_field_skins_team.sql) — the
   *  row's own name is already the team's, so this only changes the column header. */
  unit?: 'player' | 'team';
}) {
  if (result.players.length === 0) return null;

  const playerById = new Map(result.players.map((p) => [p.id, p]));
  const rows = [...result.players].sort(
    (a, b) => b.skins - a.skins || b.payoutCents - a.payoutCents || a.name.localeCompare(b.name)
  );

  return (
    <div className="card fs-card">
      <style>{`
.fs-head { margin: 0 0 10px; font-size: 13px; color: var(--ink-dim); font-weight: 600; }
.fs-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.fs-holes { display: flex; gap: 6px; margin-top: 14px; overflow-x: auto; padding-bottom: 2px; }
.fs-hole {
  flex: 0 0 auto; min-width: 40px; padding: 6px 4px; border-radius: 8px; text-align: center;
  background: var(--paper-alt); border: 1px solid var(--paper-border); font-size: 11px;
}
.fs-hole-num { font-weight: 700; color: var(--ink-dim); }
.fs-hole-win { color: var(--brass); font-weight: 800; margin-top: 2px; }
.fs-hole-carry { color: var(--ink-dim); font-style: italic; margin-top: 2px; }
.fs-hole-void { color: var(--ink-dim); opacity: .5; margin-top: 2px; }
.fs-hole-pending { opacity: .4; }
`}</style>
      <p className="fs-head">
        {basis === 'net' ? 'Net' : 'Gross'} · pot {fmtMoney(result.potCents)}
        {result.unclaimedSkins > 0
          ? ` · ${result.unclaimedSkins} skin${result.unclaimedSkins === 1 ? '' : 's'} unclaimed`
          : ''}
      </p>
      <div className="fs-scroll">
        <table>
          <thead>
            <tr>
              <th>{unit === 'team' ? 'Team' : 'Player'}</th>
              <th style={{ width: 70 }}>Skins</th>
              <th style={{ width: 90 }}>Payout</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.name}</strong>
                </td>
                <td className="tnum">{row.skins > 0 ? row.skins : '—'}</td>
                <td className="tnum">{row.payoutCents > 0 ? fmtMoney(row.payoutCents) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* One chip per hole: who won it, whether it is carrying into the next, void, or not
          decided yet. Deliberately terse — a full field's worth of holes across an 18-hole
          event is already a lot of chips in one row. */}
      <div className="fs-holes">
        {result.holes.map((h) => {
          const winner = h.winnerId ? playerById.get(h.winnerId) : undefined;
          return (
            <div key={h.hole} className={`fs-hole${h.pending ? ' fs-hole-pending' : ''}`}>
              <div className="fs-hole-num">{h.hole + 1}</div>
              {h.pending ? (
                <div className="fs-hole-void">—</div>
              ) : winner ? (
                <div className="fs-hole-win">
                  {initials(winner.name)}
                  {h.skins > 1 ? ` ×${h.skins}` : ''}
                </div>
              ) : h.carried ? (
                <div className="fs-hole-carry">carry</div>
              ) : (
                <div className="fs-hole-void">void</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FieldSkinsBoard;
